import { useState, useEffect, useCallback } from 'react';
import {
    fetchSalaryDeductions,
    DeductionRecord,
    DeductionResponse,
    DeductionParams
} from '../api/salary/deduction.service';

interface UseDeductionsResult {
    fetchData: (page: number) => Promise<void>;
    deductions: DeductionRecord[];
    loading: boolean;
    error: string | null;
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    pagination: {
        currentPage: number;
        totalPages: number;
        totalCount: number;
        pageSize: number;
    };
    setCurrentPage: (page: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    refetch: () => Promise<void>;
    clearFilters: () => void;
}

export const useDeductions = (pageSize: number = 10): UseDeductionsResult => {
    const [deductions, setDeductions] = useState<DeductionRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const fetchData = useCallback(async (
        page: number,
    ) => {
        try {
            setLoading(true);
            setError(null);

            const params: DeductionParams = {
                PageNumber: page,
                PageSize: pageSize,
            };
            
            
            const response: DeductionResponse = await fetchSalaryDeductions(params);

            if (response && response.response) {
                setDeductions(response.response);
                const totalRecords = response.totalCount || response.response.length;
                const totalPagesFromAPI = response.numberOfPages || Math.ceil(totalRecords / pageSize);

                setTotalCount(totalRecords);
                setTotalPages(totalPagesFromAPI);
            }
        } catch (err) {
            console.error("Failed to fetch deductions:", err);
            setError((err as Error).message || "Failed to load deductions");
            setDeductions([]);
        } finally {
            setLoading(false);
        }
    }, [pageSize, searchQuery]);

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, fetchData]);


    const clearFilters = useCallback(() => {
        setSearchQuery('');
        setCurrentPage(1);
        fetchData(1);
    }, [fetchData]);

    const goToNextPage = useCallback(() => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    }, [currentPage, totalPages]);

    const goToPreviousPage = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }, [currentPage]);

    const refetch = useCallback(async () => {
        await fetchData(currentPage);
    }, [currentPage, fetchData]);

    const pagination = {
        currentPage,
        totalPages,
        totalCount,
        pageSize,
    };

    return {
        deductions,
        fetchData,
        loading,
        error,
        currentPage,
        totalPages,
        totalCount,
        pageSize,
        pagination,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        refetch,
        clearFilters,
};
};